import { useCallback, useRef, MouseEvent, useEffect } from 'react';

import { DEFAULT_SCROLL_STATE } from './constants';
import { IReturnValue, IScrollState, IUseDragScrollProps } from './types';

export const useDragScroll = ({
  onSwipeLeftEdge,
  onSwipeRightEdge,
  onEdgeSwipeProgress,
  minSwipeDistance = 400,
}: IUseDragScrollProps = {}): IReturnValue => {
  const ref = useRef<HTMLDivElement>(null);
  const scrollData = useRef<IScrollState>({
    ...DEFAULT_SCROLL_STATE,
    startX: 0,
    totalDistance: 0,
    isAtEdge: false,
    edgeStartX: 0,
    readyToSwitchDay: false,
    edgeSide: null,
  });

  useEffect(() => {
    const onMouseUp = (): void => {
      const { isScrolling, isAtEdge, totalDistance, readyToSwitchDay } =
        scrollData.current;

      if (isAtEdge && isScrolling) {
        if (
          readyToSwitchDay &&
          totalDistance >= minSwipeDistance &&
          ref.current
        ) {
          const scrollLeft = ref.current.scrollLeft;
          const maxScroll = ref.current.scrollWidth - ref.current.clientWidth;

          if (scrollLeft <= 0 && onSwipeLeftEdge) {
            console.log('Switching to previous day');
            onSwipeLeftEdge();
            scrollData.current.readyToSwitchDay = false;
            scrollData.current.edgeSide = null;
          } else if (scrollLeft >= maxScroll && onSwipeRightEdge) {
            console.log('Switching to next day');
            onSwipeRightEdge();
            scrollData.current.readyToSwitchDay = false;
            scrollData.current.edgeSide = null;
          }
        } else {
          scrollData.current.readyToSwitchDay = true;
        }
      }

      scrollData.current.isScrolling = false;
      scrollData.current.totalDistance = 0;
      scrollData.current.isAtEdge = false;
      scrollData.current.edgeStartX = 0;

      if (onEdgeSwipeProgress) {
        onEdgeSwipeProgress('left', 0);
        onEdgeSwipeProgress('right', 0);
      }

      requestAnimationFrame(() => {
        if (!ref.current) return;
        ref.current.style.cursor = 'grab';
      });
    };

    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, [
    onSwipeLeftEdge,
    onSwipeRightEdge,
    minSwipeDistance,
    onEdgeSwipeProgress,
  ]);

  const onMouseDown = useCallback((e: MouseEvent) => {
    scrollData.current.isScrolling = true;
    scrollData.current.clientX = e.clientX;
    scrollData.current.clientY = e.clientY;
    scrollData.current.startX = e.clientX;
    scrollData.current.totalDistance = 0;
    scrollData.current.isAtEdge = false;
    scrollData.current.edgeStartX = 0;
  }, []);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      const { clientX, clientY, isScrolling, readyToSwitchDay } =
        scrollData.current;
      if (!isScrolling || !ref.current) return;

      requestAnimationFrame(() => {
        if (!ref.current) return;

        scrollData.current.clientX = e.clientX;
        scrollData.current.clientY = e.clientY;

        const scrollLeft = ref.current.scrollLeft;
        const maxScroll = ref.current.scrollWidth - ref.current.clientWidth;

        const isAtLeftEdge = scrollLeft <= 0;
        const isAtRightEdge = scrollLeft >= maxScroll;

        if (!isAtLeftEdge && !isAtRightEdge) {
          scrollData.current.isAtEdge = false;
          scrollData.current.totalDistance = 0;
          scrollData.current.readyToSwitchDay = false;
          scrollData.current.edgeSide = null;

          if (onEdgeSwipeProgress) {
            onEdgeSwipeProgress('left', 0);
            onEdgeSwipeProgress('right', 0);
          }
        } else {
          const currentSide = isAtLeftEdge ? 'left' : 'right';

          if (readyToSwitchDay && scrollData.current.edgeSide === currentSide) {
            if (!scrollData.current.isAtEdge) {
              scrollData.current.isAtEdge = true;
              scrollData.current.edgeStartX = e.clientX;
              scrollData.current.totalDistance = 0;
            }

            const distance = Math.abs(
              e.clientX - scrollData.current.edgeStartX
            );
            scrollData.current.totalDistance = distance;

            if (onEdgeSwipeProgress) {
              const progress = Math.min(distance / minSwipeDistance, 1);
              onEdgeSwipeProgress(currentSide, progress);
            }
          } else {
            scrollData.current.isAtEdge = true;
            scrollData.current.edgeSide = currentSide;
          }
        }

        ref.current.scrollTo({
          top: ref.current.scrollTop - e.clientY + clientY,
          left: ref.current.scrollLeft - e.clientX + clientX,
        });
        ref.current.style.cursor = 'grabbing';
      });
    },
    [onEdgeSwipeProgress, minSwipeDistance]
  );

  return {
    ref,
    onMouseDown,
    onMouseMove,
  };
};
